import { defineStore } from 'pinia'
import { ref } from 'vue'
import apiClient from '../services/authService'

export const useExamDeliveryStore = defineStore('examDelivery', () => {
    // State
    const deliveries = ref([])
    const stats = ref({})
    const loading = ref(false)
    const error = ref(null)

    // Actions
    const fetchDeliveries = async (params = {}) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get('/api/v1/examdelivery/', { params })
            deliveries.value = response.data.items || []
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao carregar entregas'
            throw err
        } finally {
            loading.value = false
        }
    }

    const searchDeliveries = async (filters = {}) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get('/api/v1/examdelivery/search', { params: filters })
            deliveries.value = response.data.items || []
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao buscar entregas'
            throw err
        } finally {
            loading.value = false
        }
    }

    const fetchDelivery = async (id) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get(`/api/v1/examdelivery/${id}/`)
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao carregar entrega'
            throw err
        } finally {
            loading.value = false
        }
    }

    const saveDeliveryDraft = async (id, deliveryData) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.post(`/api/v1/examdelivery/${id}/save-draft`, deliveryData)
            
            // Atualizar a lista local
            const index = deliveries.value.findIndex(d => d.id === id)
            if (index !== -1) {
                deliveries.value[index] = response.data
            }
            
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao salvar dados temporários'
            throw err
        } finally {
            loading.value = false
        }
    }

    const deliverExam = async (id, deliveryData) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.post(`/api/v1/examdelivery/${id}/deliver`, deliveryData)
            
            // Atualizar a lista local
            const index = deliveries.value.findIndex(d => d.id === id)
            if (index !== -1) {
                deliveries.value[index] = response.data
            }
            
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao confirmar entrega'
            throw err
        } finally {
            loading.value = false
        }
    }

    const markAsReady = async (id) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.post(`/api/v1/examdelivery/${id}/mark-ready/`)
            
            // Atualizar a lista local
            const index = deliveries.value.findIndex(d => d.id === id)
            if (index !== -1) {
                deliveries.value[index] = response.data
            }
            
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao marcar como pronto'
            throw err
        } finally {
            loading.value = false
        }
    }

    const confirmPrint = async (id, printType) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.post(`/api/v1/examdelivery/${id}/print/`, {
                print_type: printType
            })
            
            // Atualizar a lista local
            const index = deliveries.value.findIndex(d => d.id === id)
            if (index !== -1) {
                deliveries.value[index] = response.data
            }
            
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao confirmar impressão'
            throw err
        } finally {
            loading.value = false
        }
    }

    const generatePDFReport = async (id) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get(`/api/v1/examdelivery/${id}/pdf/`, {
                responseType: 'blob'
            })
            
            // Criar URL do blob e abrir em nova aba
            const blob = new Blob([response.data], { type: 'application/pdf' })
            const url = window.URL.createObjectURL(blob)
            
            // Abrir PDF em nova aba
            window.open(url, '_blank')
            
            // Limpar URL após um tempo
            setTimeout(() => {
                window.URL.revokeObjectURL(url)
            }, 1000)
            
            return response
        } catch (err) {
            error.value = err.message || 'Erro ao gerar PDF'
            throw err
        } finally {
            loading.value = false
        }
    }

    const downloadPDFReport = async (id) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get(`/api/v1/examdelivery/${id}/pdf/download/`, {
                responseType: 'blob'
            })
            
            // Criar URL do blob e fazer download
            const blob = new Blob([response.data], { type: 'application/pdf' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            
            // Extrair nome do arquivo do header Content-Disposition
            const contentDisposition = response.headers['content-disposition']
            let filename = `laudo_${id}.pdf`
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/)
                if (filenameMatch) {
                    filename = filenameMatch[1]
                }
            }
            
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
            
            return response
        } catch (err) {
            error.value = err.message || 'Erro ao baixar PDF'
            throw err
        } finally {
            loading.value = false
        }
    }

    const generateImagesPDF = async (id) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get(`/api/v1/examdelivery/${id}/images/pdf/`, {
                responseType: 'blob'
            })
            
            // Criar URL do blob e abrir em nova aba
            const blob = new Blob([response.data], { type: 'application/pdf' })
            const url = window.URL.createObjectURL(blob)
            
            // Abrir PDF em nova aba
            window.open(url, '_blank')
            
            // Limpar URL após um tempo
            setTimeout(() => {
                window.URL.revokeObjectURL(url)
            }, 1000)
            
            return response
        } catch (err) {
            error.value = err.message || 'Erro ao gerar PDF das imagens'
            throw err
        } finally {
            loading.value = false
        }
    }

    const downloadImagesPDF = async (id) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get(`/api/v1/examdelivery/${id}/images/pdf/download/`, {
                responseType: 'blob'
            })
            
            // Criar URL do blob e fazer download
            const blob = new Blob([response.data], { type: 'application/pdf' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            
            // Extrair nome do arquivo do header Content-Disposition
            const contentDisposition = response.headers['content-disposition']
            let filename = `imagens_${id}.pdf`
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/)
                if (filenameMatch) {
                    filename = filenameMatch[1]
                }
            }
            
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
            
            return response
        } catch (err) {
            error.value = err.message || 'Erro ao baixar PDF das imagens'
            throw err
        } finally {
            loading.value = false
        }
    }

    const debugImages = async (id) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get(`/api/v1/examdelivery/${id}/images/debug/`)
            console.log('Debug das imagens:', response.data)
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao debugar imagens'
            throw err
        } finally {
            loading.value = false
        }
    }

    const testSeriesImages = async (id) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get(`/api/v1/examdelivery/${id}/images/test-series/`)
            console.log('Teste das séries:', response.data)
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao testar séries'
            throw err
        } finally {
            loading.value = false
        }
    }

    const testMinioConnection = async (id) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get(`/api/v1/examdelivery/${id}/images/test-minio/`)
            console.log('Teste do MinIO:', response.data)
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao testar MinIO'
            throw err
        } finally {
            loading.value = false
        }
    }

    const testMinioEndpoint = async (id) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get(`/api/v1/examdelivery/${id}/images/test-minio-endpoint/`)
            console.log('Teste do endpoint MinIO:', response.data)
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao testar endpoint MinIO'
            throw err
        } finally {
            loading.value = false
        }
    }

    const testDicomConversion = async (id) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get(`/api/v1/examdelivery/${id}/images/test-dicom-conversion/`)
            console.log('Teste da conversão DICOM:', response.data)
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao testar conversão DICOM'
            throw err
        } finally {
            loading.value = false
        }
    }

    const fetchStats = async () => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get('/api/v1/examdelivery/stats/dashboard')
            stats.value = response.data
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao carregar estatísticas'
            throw err
        } finally {
            loading.value = false
        }
    }

    const getPatientHistory = async (patientId) => {
        loading.value = true
        error.value = null
        
        try {
            const response = await apiClient.get(`/api/v1/examdelivery/patient/${patientId}/history/`)
            return response.data
        } catch (err) {
            error.value = err.message || 'Erro ao carregar histórico do paciente'
            throw err
        } finally {
            loading.value = false
        }
    }

    // Getters
    const getDeliveryById = (id) => {
        return deliveries.value.find(d => d.id === id)
    }

    const getDeliveriesByStatus = (status) => {
        return deliveries.value.filter(d => d.status === status)
    }

    const getPendingDeliveries = () => {
        return getDeliveriesByStatus('pending')
    }

    const getReadyDeliveries = () => {
        return getDeliveriesByStatus('ready')
    }

    const getDeliveredDeliveries = () => {
        return getDeliveriesByStatus('delivered')
    }

    return {
        // State
        deliveries,
        stats,
        loading,
        error,
        
        // Actions
        fetchDeliveries,
        searchDeliveries,
        fetchDelivery,
        saveDeliveryDraft,
        deliverExam,
        markAsReady,
        confirmPrint,
        generatePDFReport,
        downloadPDFReport,
        generateImagesPDF,
        downloadImagesPDF,
        debugImages,
        testSeriesImages,
        testMinioConnection,
        testMinioEndpoint,
        testDicomConversion,
        fetchStats,
        getPatientHistory,
        
        // Getters
        getDeliveryById,
        getDeliveriesByStatus,
        getPendingDeliveries,
        getReadyDeliveries,
        getDeliveredDeliveries
    }
})